import React, { useState, useEffect } from 'react';
import api from '../../utils/axios';
import { formatPrice } from '../../utils/priceFormatter';
import { downloadQuotationHTML } from '../../utils/quotationHtmlGenerator';
import { showSuccess, showError, showConfirm, showLoading, hideLoading } from '../../utils/toast';
import { FaEye, FaDownload, FaTrash, FaSearch, FaFilter } from 'react-icons/fa';

const API_BASE = process.env.REACT_APP_BASE_URL || "http://localhost:5000/";

const AdminQuotations = () => {
  const [quotations, setQuotations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedQuotation, setSelectedQuotation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    fetchQuotations();
  }, []);

  const fetchQuotations = async () => {
    try {
      setIsLoading(true);
      const response = await api.get('/quotations');
      setQuotations(response.data);
    } catch (error) {
      console.error('Error fetching quotations:', error);
      showError('Failed to fetch quotations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewQuotation = (quotation) => {
    setSelectedQuotation(quotation);
    setShowModal(true);
  };

  const handleDownloadQuotation = async (quotation) => {
    try {
      showLoading('Generating Quotation...');

      // Record the download
      await api.put(`/quotations/${quotation.id}/download`);

      // Generate and download the HTML
      await downloadQuotationHTML(quotation);

      // Update the download count in the local state
      setQuotations(quotations.map(q =>
        q.id === quotation.id ? {
          ...q,
          download_count: (q.download_count || 0) + 1,
          last_downloaded_at: new Date().toISOString()
        } : q
      ));

      hideLoading();
      showSuccess('Quotation downloaded successfully!');
    } catch (error) {
      console.error('Error downloading quotation:', error);
      hideLoading();
      showError('Failed to download quotation. Please try again.');
    }
  };

  const handleDeleteQuotation = async (quotationId) => {
    const result = await showConfirm(
      'Delete Quotation',
      'Are you sure you want to delete this quotation? This action cannot be undone.',
      'Yes, Delete',
      'Cancel'
    );

    if (result.isConfirmed) {
      try {
        showLoading('Deleting quotation...');
        await api.delete(`/quotations/${quotationId}`);
        setQuotations(quotations.filter(q => q.id !== quotationId));
        hideLoading();
        showSuccess('Quotation deleted successfully');
      } catch (error) {
        console.error('Error deleting quotation:', error);
        hideLoading();
        showError('Failed to delete quotation');
      }
    }
  };

  const handleStatusUpdate = async (quotationId, newStatus) => {
    try {
      await api.put(`/quotations/${quotationId}/status`, { status: newStatus });
      setQuotations(quotations.map(q =>
        q.id === quotationId ? { ...q, status: newStatus } : q
      ));
      showSuccess('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      showError('Failed to update status');
    }
  };

  const filteredQuotations = quotations.filter(quotation => {
    const matchesSearch = quotation.quotation_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quotation.customer_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quotation.customer_email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = !statusFilter || quotation.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status) => {
    switch (status) {
      case 'draft': return '#6c757d';
      case 'sent': return '#007bff';
      case 'viewed': return '#17a2b8';
      case 'accepted': return '#28a745';
      case 'rejected': return '#dc3545';
      case 'expired': return '#ffc107';
      default: return '#6c757d';
    }
  };

  const getFirstImageUrl = (item) => {
    let imageUrl = "";
    if (item.product_image) {
      imageUrl = item.product_image;
      if (!imageUrl.startsWith("http")) {
        imageUrl = API_BASE + imageUrl.replace(/^\/+/, "");
      }
    }
    return imageUrl;
  };

  if (isLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div style={{
          width: 40,
          height: 40,
          border: '3px solid #f3f3f3',
          borderTop: '3px solid #333',
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
          margin: '0 auto 16px'
        }}></div>
        Loading quotations...
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      <div style={{ marginBottom: '20px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '20px' }}>
          Quotation Management
        </h1>

        {/* Search and Filter */}
        <div style={{
          display: 'flex',
          gap: '16px',
          marginBottom: '20px',
          flexWrap: 'wrap'
        }}>
          <div style={{ position: 'relative', flex: '1', minWidth: '300px' }}>
            <FaSearch style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#666',
              fontSize: '14px'
            }} />
            <input
              type="text"
              placeholder="Search quotations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 12px 10px 40px',
                border: '1px solid #ddd',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: '10px 12px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              minWidth: '150px'
            }}
          >
            <option value="">All Status</option>
            <option value="draft">Draft</option>
            <option value="sent">Sent</option>
            <option value="viewed">Viewed</option>
            <option value="accepted">Accepted</option>
            <option value="rejected">Rejected</option>
            <option value="expired">Expired</option>
          </select>
        </div>
      </div>

      {/* Quotations Table */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                background: '#f8f9fa',
                borderBottom: '1px solid #e9ecef'
              }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Quotation #</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Customer</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Amount</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Created</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Downloads</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredQuotations.map((quotation) => (
                <tr key={quotation.id} style={{
                  borderBottom: '1px solid #f1f3f4'
                }}>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {quotation.quotation_number}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div>
                      <div style={{ fontWeight: '500', color: '#333' }}>
                        {quotation.customer_name}
                      </div>
                      <div style={{ fontSize: '12px', color: '#666' }}>
                        {quotation.customer_email}
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontWeight: '600', color: '#333' }}>
                      {formatPrice(quotation.total_amount)}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <select
                      value={quotation.status}
                      onChange={(e) => handleStatusUpdate(quotation.id, e.target.value)}
                      style={{
                        padding: '4px 8px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '12px',
                        color: getStatusColor(quotation.status),
                        fontWeight: '500',
                        background: '#fff'
                      }}
                    >
                      <option value="draft">Draft</option>
                      <option value="sent">Sent</option>
                      <option value="viewed">Viewed</option>
                      <option value="accepted">Accepted</option>
                      <option value="rejected">Rejected</option>
                      <option value="expired">Expired</option>
                    </select>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {new Date(quotation.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td style={{ padding: '16px' }}>
                    <div style={{ fontSize: '12px', color: '#666' }}>
                      {quotation.download_count} times
                    </div>
                  </td>
                  <td style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                      <button
                        onClick={() => handleViewQuotation(quotation)}
                        style={{
                          background: '#007bff',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="View Details"
                      >
                        <FaEye size={12} />
                      </button>
                      <button
                        onClick={() => handleDownloadQuotation(quotation)}
                        style={{
                          background: '#28a745',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Download"
                      >
                        <FaDownload size={12} />
                      </button>
                      <button
                        onClick={() => handleDeleteQuotation(quotation.id)}
                        style={{
                          background: '#dc3545',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '4px',
                          padding: '6px 8px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                        title="Delete"
                      >
                        <FaTrash size={12} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredQuotations.length === 0 && (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            color: '#666'
          }}>
            No quotations found
          </div>
        )}
      </div>

      {/* Quotation Details Modal */}
      {showModal && selectedQuotation && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: '#fff',
            borderRadius: '12px',
            maxWidth: '800px',
            width: '100%',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{
              padding: '20px',
              borderBottom: '1px solid #e9ecef',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '600' }}>
                Quotation Details - {selectedQuotation.quotation_number}
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                ×
              </button>
            </div>

            <div style={{ padding: '20px' }}>
              {/* Customer Details */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Customer Information
                </h3>
                <div style={{
                  background: '#f8f9fa',
                  padding: '16px',
                  borderRadius: '8px',
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '12px'
                }}>
                  <div>
                    <strong>Name:</strong> {selectedQuotation.customer_name}
                  </div>
                  <div>
                    <strong>Email:</strong> {selectedQuotation.customer_email}
                  </div>
                  <div>
                    <strong>Phone:</strong> {selectedQuotation.customer_phone}
                  </div>
                  <div>
                    <strong>Valid Until:</strong> {new Date(selectedQuotation.valid_until).toLocaleDateString()}
                  </div>
                </div>
              </div>

              {/* Items */}
              <div style={{ marginBottom: '24px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px' }}>
                  Quotation Items ({selectedQuotation.items?.length || 0})
                </h3>
                {selectedQuotation.items?.map((item, index) => {
                  const imageUrl = getFirstImageUrl(item);
                  return (
                    <div key={index} style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      padding: '12px',
                      border: '1px solid #e9ecef',
                      borderRadius: '8px',
                      marginBottom: '8px'
                    }}>
                      <div style={{
                        width: '50px',
                        height: '50px',
                        borderRadius: '6px',
                        overflow: 'hidden',
                        background: '#eee',
                        flexShrink: 0
                      }}>
                        {imageUrl ? (
                          <img
                            src={imageUrl}
                            alt={item.product_name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover'
                            }}
                          />
                        ) : (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            height: '100%',
                            color: '#bbb',
                            fontSize: '10px'
                          }}>
                            No Image
                          </div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                          {item.product_name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {formatPrice(item.unit_price)} × {item.quantity}
                        </div>
                      </div>
                      <div style={{ fontWeight: '600' }}>
                        {formatPrice(item.total_price)}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Total */}
              <div style={{
                background: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                textAlign: 'right'
              }}>
                <div style={{ fontSize: '18px', fontWeight: '600' }}>
                  Total: {formatPrice(selectedQuotation.total_amount)}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminQuotations;
